import type { Domain } from "../../domain/contracts";
import {
  VISITED_ISLAND_CACHE_POLICIES,
  type VisitedIslandCachePolicy
} from "./islandCachePolicy";

export interface IslandCacheMigrationCache {
  keys(): Promise<readonly Request[]>;
  match(request: RequestInfo | URL): Promise<Response | undefined>;
  put(request: RequestInfo | URL, response: Response): Promise<void>;
}

export interface IslandCacheCleanupStorage {
  delete(cacheName: string): Promise<boolean>;
  keys(): Promise<string[]>;
  open(cacheName: string): Promise<IslandCacheMigrationCache>;
}

export type IslandCacheRefresh = (request: Request) => Promise<Response>;

const CURRENT_CACHE_NAMES = new Set(
  VISITED_ISLAND_CACHE_POLICIES.map(({ cacheName }) => cacheName)
);

export async function cleanupOutdatedVisitedIslandCaches(
  cacheStorage: IslandCacheCleanupStorage | undefined = readCacheStorage(),
  refresh: IslandCacheRefresh = refreshCacheRequest
): Promise<string[]> {
  if (cacheStorage === undefined) {
    return [];
  }

  const cacheNames = await cacheStorage.keys();
  const outdatedCaches = cacheNames.flatMap((cacheName) => {
    const policy = getVisitedIslandCachePolicy(cacheName);
    return policy === null || CURRENT_CACHE_NAMES.has(cacheName)
      ? []
      : [{ cacheName, policy }];
  });
  const deletedCacheNames: string[] = [];

  for (const { cacheName, policy } of outdatedCaches) {
    const replacementReady = await migrateOutdatedCache(
      cacheStorage,
      cacheName,
      policy,
      refresh
    );

    if (replacementReady && (await cacheStorage.delete(cacheName))) {
      deletedCacheNames.push(cacheName);
    }
  }

  return deletedCacheNames;
}

async function migrateOutdatedCache(
  cacheStorage: IslandCacheCleanupStorage,
  outdatedCacheName: string,
  policy: VisitedIslandCachePolicy,
  refresh: IslandCacheRefresh
): Promise<boolean> {
  try {
    const [outdatedCache, currentCache] = await Promise.all([
      cacheStorage.open(outdatedCacheName),
      cacheStorage.open(policy.cacheName)
    ]);
    const requests = await outdatedCache.keys();
    let replacementReady = true;

    for (const request of requests) {
      const cachedResponse = await outdatedCache.match(request);
      if (cachedResponse === undefined) {
        replacementReady = false;
      } else {
        await currentCache.put(request, cachedResponse.clone());
      }
    }

    for (const request of requests) {
      try {
        const refreshedResponse = await refresh(request);
        if (!refreshedResponse.ok) {
          replacementReady = false;
          continue;
        }

        await currentCache.put(request, refreshedResponse);
      } catch {
        replacementReady = false;
      }
    }

    return replacementReady;
  } catch {
    return false;
  }
}

function getVisitedIslandCachePolicy(
  cacheName: string
): VisitedIslandCachePolicy | null {
  return (
    VISITED_ISLAND_CACHE_POLICIES.find(({ domain }) =>
      cacheName.startsWith(cachePrefix(domain))
    ) ?? null
  );
}

function cachePrefix(domain: Domain): string {
  return "little-launch-island-" + domain + "-";
}

function refreshCacheRequest(request: Request): Promise<Response> {
  return fetch(request, { cache: "reload" });
}

function readCacheStorage(): IslandCacheCleanupStorage | undefined {
  return typeof caches === "undefined" ? undefined : caches;
}
